'use client';

import { useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '../utils/cn';

type PartnerFilter = 'stats' | 'referrals';

interface PartnershipTabScreenProps {
  isLoading?: boolean;
  referralBalanceUsd?: string | null;
  progressPercent: number;
  commissionPercentText: string;
  nextLevelText?: string | null;
  canConvert: boolean;
  isConverting?: boolean;
  canWithdraw: boolean;
  totalReferrals: number;
  turnoverFormatted: string;
  onConvertClick: () => void;
  onWithdrawClick: () => void;
  onInviteClick: () => void;
  referralsContent?: ReactNode;
  overlays?: ReactNode;
}

export function PartnershipTabScreen({
  isLoading = false,
  referralBalanceUsd,
  progressPercent,
  commissionPercentText,
  nextLevelText,
  canConvert,
  isConverting = false,
  canWithdraw,
  totalReferrals,
  turnoverFormatted,
  onConvertClick,
  onWithdrawClick,
  onInviteClick,
  referralsContent,
  overlays,
}: PartnershipTabScreenProps) {
  const [partnerFilter, setPartnerFilter] = useState<PartnerFilter>('stats');

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <div className="h-40 animate-pulse rounded-xl bg-secondary" />
        <div className="h-32 animate-pulse rounded-xl bg-secondary" />
        <div className="h-24 animate-pulse rounded-xl bg-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 pb-20 pt-2">
      <div
        className="relative overflow-hidden rounded-3xl p-4"
        style={{
          background: 'rgba(60,60,60,0.35)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow:
            'inset 0 1px 0 0 rgba(255,255,255,0.1), inset 0 -1px 0 0 rgba(255,255,255,0.02)',
        }}
      >
        <div className="pointer-events-none absolute -right-8 -top-8 h-[214px] w-[218px] rounded-full bg-[#f5d500] opacity-[0.12] blur-[76px]" />
        <div className="pointer-events-none absolute -left-12 bottom-0 h-[180px] w-[180px] rounded-full bg-[#d9b04a] opacity-[0.08] blur-[60px]" />

        <div className="relative z-[1] space-y-2.5">
          <div>
            <p className="font-benzin text-xs text-[#f2f2f7]">Заработано</p>
            <div className="mt-1 flex items-center gap-1">
              <p className="font-benzin text-[28px] font-semibold leading-none text-white">
                ${referralBalanceUsd || '0.00'}
              </p>
            </div>
          </div>

          <div className="space-y-[9px]">
            <div className="relative h-3 overflow-hidden rounded-md bg-black">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="h-full rounded-md bg-brand-orange"
              />
              {[0, 33.33, 66.67, 100].map((position) => (
                <div
                  key={position}
                  className="absolute top-1 h-1 w-1 rounded-full bg-[#282828]"
                  style={{ left: `${position}%` }}
                />
              ))}
            </div>

            <div className="flex justify-between">
              {['20%', '30%', '40%', '50%'].map((item) => (
                <span key={item} className="text-[10px] text-[#aeaeb2]">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="space-y-0.5">
            <p className="text-sm font-bold text-white">
              Ваш уровень комиссии {commissionPercentText}
            </p>
            {nextLevelText && (
              <p className="text-xs text-white">{nextLevelText}</p>
            )}
          </div>

          <div className="flex gap-4 pb-2 pt-1">
            <button
              onClick={onConvertClick}
              disabled={!canConvert || isConverting}
              className="flex h-14 flex-1 items-center justify-center rounded-[20px] text-base font-semibold text-white disabled:opacity-50"
              style={{
                background: 'rgba(32,32,32,0.6)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow:
                  '0 4px 3.5px rgba(0,0,0,0.25), inset 0 1px 0 0 rgba(255,255,255,0.1)',
              }}
            >
              {isConverting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              В кредиты
            </button>
            <button
              onClick={onWithdrawClick}
              disabled={!canWithdraw}
              className="flex h-14 flex-1 items-center justify-center rounded-[20px] text-base font-semibold text-white disabled:opacity-50"
              style={{
                background: 'rgba(32,32,32,0.6)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow:
                  '0 4px 3.5px rgba(0,0,0,0.25), inset 0 1px 0 0 rgba(255,255,255,0.1)',
              }}
            >
              Вывод
            </button>
          </div>

          <button
            onClick={onInviteClick}
            className="flex h-14 w-full items-center justify-center rounded-[20px] bg-[#ef6444] text-base font-semibold text-white shadow-lg"
          >
            Пригласите друзей
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        {[
          { id: 'stats' as const, label: 'Статистика' },
          { id: 'referrals' as const, label: 'Рефералы' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setPartnerFilter(tab.id)}
            className={cn(
              'flex-shrink-0 rounded-[20px] px-4 py-2 text-xs transition-colors',
              partnerFilter === tab.id
                ? 'bg-[#fafafa] text-[#0f0f0f]'
                : 'bg-[#242424]/9 text-[#fafafa]',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="divide-y divide-white/5 rounded-xl bg-white/5">
        {partnerFilter === 'stats' ? (
          <>
            <StatRow
              label="Получено благодаря рефералам"
              value={`$${referralBalanceUsd || '0'}`}
            />
            <StatRow
              label="Приглашенные друзья"
              value={String(totalReferrals)}
            />
            <StatRow label="Покупки рефералов" value={turnoverFormatted} />
          </>
        ) : (
          referralsContent || (
            <div className="px-4 py-6 text-center text-sm text-[#8e8e93]">
              Список рефералов скоро появится
            </div>
          )
        )}
      </div>

      {overlays}
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div>
        <p className="text-xs text-[#8e8e93]">{label}</p>
        <p
          className="mt-0.5 text-base font-semibold text-[#e7e8ea]"
          style={{ fontFamily: 'Open Sans, Inter, sans-serif' }}
        >
          {value}
        </p>
      </div>
    </div>
  );
}
